import React, { useCallback } from 'react';
import styled from 'styled-components';
import Button from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

const StyledDiv = styled.div<{
  disabled?: boolean;
  disabledClearButton?: boolean;
  disabledAddButton?: boolean;
  maxWidth?: string;
  margin?: string;
}>`
  display: flex;
  align-items: center;
  border: 1px solid ${COLORS.greyBase};
  border-radius: 4px 4px 0px 0px;
  margin-bottom: 24px;
  width: 100%;
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth};` : '')}
  margin-right: auto;
  margin-left: auto;
  ${({ margin }) => (margin ? `margin: ${margin};` : '')}
  input {
    font-size: 16px;
    border: none;
    outline: none;
    flex: 1 1 auto;
    background: url('/images/search-icon.png') no-repeat left;
    background-size: 19px;
    background-position-x: 14px;
    padding: 13px 13px 13px 44px;
  }
  .input-button {
    margin-right: 8px;
    opacity: ${({ disabled }) => (disabled ? 0.2 : 1)};
    transition: opacity 0.3s, visibility 0.3s;
  }
  & .add-button {
    opacity: ${({ disabledAddButton }) => (disabledAddButton ? 0.2 : 1)};
  }
  & .clear-button {
    color: ${COLORS.blackBase};
    opacity: ${({ disabledClearButton }) => (disabledClearButton ? 0.2 : 1)};
    outline-color: ${COLORS.blackBase};
  }
`;

interface SearchboxProps {
  value: string;
  type?: string;
  className?: string;
  disabled?: boolean;
  disabledClearButton?: boolean;
  disabledAddButton?: boolean;
  placeholder: string;
  autoFocus?: boolean;
  maxWidth?: string;
  margin?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  children?: React.ReactNode | React.ReactNode[];
  onClear?: () => unknown;
  onAdd?: () => unknown;
  onEnterPress?: () => unknown;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const TrueImpactSearchBox: React.FC<SearchboxProps> = ({
  type,
  value,
  disabledAddButton,
  disabledClearButton,
  disabled,
  placeholder,
  autoFocus,
  maxWidth,
  margin,
  inputRef,
  children,
  className,
  onEnterPress,
  onAdd,
  onClear,
  onChange,
}) => {
  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (onEnterPress && e.key === 'Enter') {
        onEnterPress();
      }
    },
    [onEnterPress],
  );

  return (
    <StyledDiv
      margin={margin}
      maxWidth={maxWidth}
      disabled={disabled}
      disabledClearButton={disabledClearButton}
      disabledAddButton={disabledAddButton}
      className={className}
    >
      <input
        ref={inputRef}
        placeholder={placeholder}
        type={type || 'text'}
        value={value}
        autoFocus={autoFocus}
        onChange={onChange}
        onKeyUp={handleKeyUp}
        disabled={disabled}
      />
      {children}
      {onAdd && (
        <Button
          variant="secondary-small"
          onClick={onAdd}
          disabled={disabledAddButton}
          className="input-button add-button"
        >
          Add
        </Button>
      )}
      {onClear && (
        <Button
          variant="secondary-small"
          onClick={onClear}
          disabled={disabledClearButton}
          className="input-button clear-button"
        >
          Clear
        </Button>
      )}
    </StyledDiv>
  );
};

export default TrueImpactSearchBox;
